<template>
  <div class="dashboard-panel">
    <!-- Skeleton (no data + no geo) -->
    <div v-if="!hasData && !geoLoaded" class="heatmap-skeleton">
      <div class="heatmap-skeleton__map" />
    </div>

    <!-- Layout: PC = ranking + chart split / Mobile = single column -->
    <div v-else class="region-heatmap-layout" :class="{ 'is-mobile': props.isMobile }">
      <!-- LEFT: Ranking Table (PC only, geoLoaded — 데이터 0건이어도 grid 폭 유지를 위해 항상 렌더링) -->
      <div v-if="!props.isMobile && geoLoaded" class="region-ranking">
        <table class="region-ranking__table">
          <thead>
            <tr>
              <th class="region-ranking__th-region">{{ t('dashboard.regionDetail.ranking') }}</th>
              <th class="region-ranking__th-num">{{ t('dashboard.regionDetail.received') }}</th>
              <th class="region-ranking__th-num">{{ t('dashboard.regionDetail.completed') }}</th>
              <th class="region-ranking__th-num">{{ t('dashboard.regionDetail.completionRate') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(item, idx) in sortedRegions"
              :key="item.mainName"
              :class="{ 'is-selected': isSelected(item.mainName) }"
              @click.stop="onRegionClick(item)"
            >
              <td class="region-ranking__td-region">
                <span v-if="idx < 3" class="region-ranking__badge" :class="`region-ranking__badge--${idx + 1}`">{{ idx + 1 }}</span>
                <span v-else class="region-ranking__rank-spacer" />
                {{ toShortName(item.mainName) }}
              </td>
              <td class="region-ranking__td-num">{{ deriveRegionMetrics(item).total }}</td>
              <td class="region-ranking__td-num">{{ deriveRegionMetrics(item).completed }}</td>
              <td class="region-ranking__td-num region-ranking__td-rate">{{ formatRate(deriveRegionMetrics(item).completionRate) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- RIGHT: Chart Wrapper (popover overlays this area) -->
      <div class="region-heatmap-chart-wrap">
        <!-- ECharts Map (geo 로드 성공 시 항상 표시) -->
        <div v-if="geoLoaded" class="heatmap-chart">
          <v-chart
            :option="mapOption"
            :style="chartStyle"
            autoresize
            @click="onChartClick"
          />
        </div>

        <!-- Geo 로드 실패 placeholder — 데이터는 있으나 GeoJSON 미로드 (SURINP-288) -->
        <div v-else-if="hasData" class="heatmap-geo-error" :style="chartStyle">
          {{ t('dashboard.regionDetail.geoLoadError') }}
        </div>

        <!-- Popover (non-modal, no backdrop) -->
        <div
          v-if="popoverState"
          ref="popoverRef"
          class="region-popover"
          :class="{ 'is-mobile': props.isMobile }"
          role="dialog"
          aria-modal="false"
          @click.stop
        >
          <div class="region-popover__header">
            <span class="region-popover__dot" />
            <strong class="region-popover__name">{{ popoverState.name }}</strong>
            <button
              type="button"
              class="region-popover__close"
              :aria-label="t('dashboard.regionDetail.close')"
              @click.stop="closePopover"
            >×</button>
          </div>
          <div class="region-popover__metrics">
            <div class="region-popover__metric">
              <div class="region-popover__metric-label">{{ t('dashboard.regionDetail.received') }}</div>
              <div class="region-popover__metric-value is-received">{{ popoverState.metrics.total }}</div>
            </div>
            <div class="region-popover__metric">
              <div class="region-popover__metric-label">{{ t('dashboard.regionDetail.completed') }}</div>
              <div class="region-popover__metric-value is-completed">{{ popoverState.metrics.completed }}</div>
            </div>
            <div class="region-popover__metric">
              <div class="region-popover__metric-label">{{ t('dashboard.regionDetail.completionRate') }}</div>
              <div class="region-popover__metric-value is-rate">{{ formatRate(popoverState.metrics.completionRate) }}</div>
            </div>
            <div class="region-popover__metric">
              <div class="region-popover__metric-label">{{ t('dashboard.regionDetail.pending') }}</div>
              <div class="region-popover__metric-value is-pending">{{ popoverState.metrics.pending }}</div>
            </div>
          </div>
          <div class="region-popover__progress">
            <div
              class="region-popover__progress-fill"
              :style="{ width: progressWidth + '%' }"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
/* global defineProps */
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import i18n from '@/locales/i18n'
import { use, registerMap } from 'echarts/core'
import { MapChart } from 'echarts/charts'
import { VisualMapComponent, TooltipComponent, GeoComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import VChart from 'vue-echarts'

use([CanvasRenderer, MapChart, VisualMapComponent, TooltipComponent, GeoComponent])

const t = i18n.global.t

const props = defineProps({
  data: { type: Object, default: null },
  isPcRedesign: { type: Boolean, default: false },
  isMobile: { type: Boolean, default: false },
  /**
   * 지도 차트 영역 높이(px). 기본 720(대시보드 split layout 기준).
   * report 등 다른 컨텍스트에 임베드 시 더 작은 값 권장 (예: 420).
   */
  chartHeight: { type: Number, default: 720 },
  /**
   * ECharts map series zoom 배율. 기본 1.6 (대시보드).
   * 좁은 컨테이너(report 등)에서는 1.0 권장.
   */
  mapZoom: { type: Number, default: 1.6 },
  /**
   * ECharts map series layoutSize. 기본 '110%' (대시보드).
   * 좁은 컨테이너에서는 '95%' / '90%' 권장 (서울·제주 잘림 방지).
   */
  mapLayoutSize: { type: [String, Number], default: '110%' }
})

/* eslint-disable quote-props */
const REGION_SHORT = {
  '서울특별시': '서울',
  '부산광역시': '부산',
  '대구광역시': '대구',
  '인천광역시': '인천',
  '광주광역시': '광주',
  '대전광역시': '대전',
  '울산광역시': '울산',
  '세종특별자치시': '세종',
  '경기도': '경기',
  '강원도': '강원',
  '강원특별자치도': '강원',
  '충청북도': '충북',
  '충청남도': '충남',
  '전라북도': '전북',
  '전북특별자치도': '전북',
  '전라남도': '전남',
  '경상북도': '경북',
  '경상남도': '경남',
  '제주특별자치도': '제주'
}
/* eslint-enable quote-props */

function toShortName(mainName) {
  return REGION_SHORT[mainName] || mainName
}

const geoLoaded = ref(false)

const regions = computed(() => props.data?.regions || [])
const hasData = computed(() => regions.value.length > 0)

const maxCount = computed(() => {
  if (!hasData.value) return 1
  return Math.max(...regions.value.map((d) => Number(d.totalRequests) || 0), 1)
})

const sortedRegions = computed(() => {
  if (!hasData.value) return []
  return [...regions.value].sort((a, b) => (Number(b.totalRequests) || 0) - (Number(a.totalRequests) || 0))
})

/**
 * 백엔드 응답 신규 스키마 (SURINP-211 cs-back 변경 후):
 *   - totalRequests: 처리 대상 전체 (취소/이관 제외) — 의미 변경
 *   - completedCount: 완료 건수 (R_COMPLETE/R_CONFIRM)
 *   - pendingCount: 미처리 건수 (취소/이관 3종 제외)
 *   - completionRate: 0.0~100.0 (정합성 totalRequests = completedCount + pendingCount 보장)
 *
 * 구버전 응답(`completedRequests`)은 캐시/배포 타이밍 보호용 fallback. 백엔드 배포 후
 * 도달 가능성 낮지만 defensive coding으로 유지.
 */
function deriveRegionMetrics(r) {
  const total = Number(r.totalRequests) || 0
  const completed = r.completedCount != null
    ? Number(r.completedCount)
    : (Number(r.completedRequests) || 0)
  const pending = r.pendingCount != null
    ? Number(r.pendingCount)
    : Math.max(total - completed, 0)
  const completionRate = r.completionRate != null
    ? Number(r.completionRate)
    : (total > 0 ? (completed / total) * 100 : 0)
  return { total, completed, pending, completionRate }
}

function formatRate(rate) {
  if (rate == null || isNaN(rate)) return '0.0%'
  return Number(rate).toFixed(1) + '%'
}

const popoverState = ref(null)
const popoverRef = ref(null)

function isSelected(mainName) {
  return popoverState.value?.mainName === mainName
}

const progressWidth = computed(() => {
  if (!popoverState.value) return 0
  const r = popoverState.value.metrics.completionRate
  return Math.max(0, Math.min(100, Number(r) || 0))
})

function onRegionClick(item) {
  popoverState.value = {
    mainName: item.mainName,
    name: toShortName(item.mainName),
    metrics: deriveRegionMetrics(item)
  }
}

function onChartClick(params) {
  if (!params || params.componentType !== 'series') return
  const region = regionLookup.value[params.name]
  if (region) onRegionClick(region)
}

function closePopover() {
  popoverState.value = null
}

function onDocumentClick(e) {
  if (!popoverState.value) return
  if (popoverRef.value && popoverRef.value.contains(e.target)) return
  closePopover()
}

onMounted(async () => {
  document.addEventListener('click', onDocumentClick, true)
  try {
    const geoJson = await import('@/assets/geo/korea-provinces.json')
    registerMap('korea', geoJson.default || geoJson)
    geoLoaded.value = true
  } catch (err) {
    window.__APP_LOG__('GeoJSON load failed, using fallback view')
    geoLoaded.value = false
  }
})

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClick, true)
})

const regionLookup = computed(() => {
  const map = {}
  regions.value.forEach((r) => {
    map[toShortName(r.mainName)] = r
  })
  return map
})

const mapOption = computed(() => {
  const seriesData = hasData.value
    ? regions.value.map((d) => ({
      name: toShortName(d.mainName),
      value: Number(d.totalRequests) || 0
    }))
    : []

  return {
    tooltip: {
      trigger: 'item',
      formatter: (params) => {
        const region = regionLookup.value[params.name]
        if (region) {
          const m = deriveRegionMetrics(region)
          return `<strong>${params.name}</strong><br/>` +
            `${t('dashboard.regionDetail.received')}: ${m.total}<br/>` +
            `${t('dashboard.regionDetail.completed')}: ${m.completed}<br/>` +
            `${t('dashboard.regionDetail.completionRate')}: ${formatRate(m.completionRate)}`
        }
        return `${params.name}: <strong>${params.value || 0}${t('dashboard.cases')}</strong>`
      }
    },
    visualMap: {
      min: 0,
      max: maxCount.value,
      left: 'left',
      top: 'center',
      text: [t('dashboard.total'), '0'],
      textStyle: { fontSize: 11, color: '#5C616B' },
      inRange: {
        color: ['#C5D1E3', '#9DB2CF', '#5A7CAD', '#3B5998', '#1E3058']
      },
      calculable: true
    },
    series: [
      {
        name: t('dashboard.regionHeatmap'),
        type: 'map',
        map: 'korea',
        // 지도 고정 — 마우스 드래그(pan)/스크롤(zoom)로 위치·확대가 변하지 않게 (SURINP-288/292).
        roam: false,
        zoom: props.mapZoom,
        layoutCenter: ['58%', '50%'],
        layoutSize: props.mapLayoutSize,
        label: {
          show: true,
          fontSize: 12,
          color: '#424750',
          formatter: '{b}'
        },
        emphasis: {
          label: { show: true, fontWeight: 'bold', fontSize: 14 },
          itemStyle: { areaColor: '#3D7E9A' }
        },
        itemStyle: {
          borderColor: '#fff',
          borderWidth: 1.5
        },
        data: seriesData
      }
    ]
  }
})

const chartStyle = computed(() => ({
  width: '100%',
  height: props.isMobile ? '450px' : `${props.chartHeight}px`
}))
</script>

<style scoped>
/* ========================================
   RegionHeatmap
   ======================================== */
.dashboard-panel {
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border-radius: var(--radius-xl, 16px);
  border: 1px solid var(--color-gray-100, #E8EAED);
  box-shadow: var(--shadow-sm, 0 1px 2px rgba(0, 0, 0, 0.08));
  padding: 24px;
  transition: box-shadow var(--transition-normal, 0.2s ease), transform var(--transition-normal, 0.2s ease);
}

.dashboard-panel:hover {
  box-shadow: var(--shadow-md, 0 2px 6px rgba(0, 0, 0, 0.1));
  transform: translateY(-1px);
}

.dashboard-panel__title {
  font-size: 15px;
  font-weight: 700;
  color: var(--color-gray-700, #2E333B);
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.dashboard-panel__title::before {
  content: '';
  width: 4px;
  height: 16px;
  background: var(--color-primary, #3B5998);
  border-radius: 2px;
  flex-shrink: 0;
}

/* ===== Layout: PC split / Mobile single column ===== */
.region-heatmap-layout {
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 24px;
  align-items: stretch;
}

.region-heatmap-layout.is-mobile {
  grid-template-columns: 1fr;
}

/* 태블릿(768~1279): 위젯 1col 폭에 맞춰 사이드바+지도 2열 → 1열 (형제 패널과 동일 분기) */
@media (min-width: 768px) and (max-width: 1279px) {
  .region-heatmap-layout {
    grid-template-columns: 1fr;
  }
}

.region-heatmap-chart-wrap {
  position: relative;
  min-width: 0;
}

/* ===== Skeleton ===== */
.heatmap-skeleton {
  padding: 20px 0;
  display: flex;
  justify-content: center;
}

.heatmap-skeleton__map {
  width: 280px;
  height: 360px;
  border-radius: var(--radius-lg, 12px);
  background: linear-gradient(90deg, #E8EAED 25%, #F2F3F5 50%, #E8EAED 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* ===== Ranking Table (PC) ===== */
.region-ranking {
  overflow-y: auto;
  max-height: 650px;
  font-size: 13px;
}

.region-ranking__table {
  width: 100%;
  border-collapse: collapse;
}

.region-ranking__table thead th {
  position: sticky;
  top: 0;
  background: #fff;
  font-size: 12px;
  font-weight: 600;
  color: var(--color-gray-500, #5C616B);
  padding: 10px 8px;
  border-bottom: 1px solid var(--color-gray-100, #E8EAED);
  text-align: left;
}

.region-ranking__th-num {
  text-align: right;
}

.region-ranking__table tbody tr {
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.region-ranking__table tbody tr:hover {
  background-color: var(--color-gray-50, #F7F8FA);
}

.region-ranking__table tbody tr.is-selected {
  background-color: var(--color-primary-50, #EEF2FA);
}

.region-ranking__td-region,
.region-ranking__td-num {
  padding: 8px;
  border-bottom: 1px solid var(--color-gray-100, #E8EAED);
  vertical-align: middle;
}

.region-ranking__td-region {
  font-weight: 600;
  color: var(--color-gray-700, #2E333B);
  display: flex;
  align-items: center;
  gap: 8px;
}

.region-ranking__td-num {
  text-align: right;
  font-variant-numeric: tabular-nums;
  color: var(--color-gray-700, #2E333B);
  font-weight: 600;
}

.region-ranking__td-rate {
  color: var(--color-primary, #3B5998);
}

.region-ranking__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  flex-shrink: 0;
}

.region-ranking__badge--1 { background: #C0392B; }
.region-ranking__badge--2 { background: #D68910; }
.region-ranking__badge--3 { background: #2874A6; }

.region-ranking__rank-spacer {
  display: inline-block;
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}

/* ===== Chart ===== */
.heatmap-chart {
  display: flex;
  justify-content: center;
}

/* ===== Geo 로드 실패 placeholder (SURINP-288) ===== */
.heatmap-geo-error {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  font-size: 14px;
  color: var(--color-gray-500, #5C616B);
  background: var(--color-gray-50, #F7F8FA);
  border: 1px dashed var(--color-gray-200, #DCDFE3);
  border-radius: var(--radius-lg, 12px);
  text-align: center;
  padding: 24px;
}

/* ===== Popover (non-modal, no backdrop) ===== */
.region-popover {
  position: absolute;
  top: 16px;
  left: 16px;
  width: 280px;
  background: #fff;
  border: 1px solid var(--color-gray-200, #DCDFE3);
  border-radius: 12px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
  padding: 16px;
  z-index: 5;
}

.region-popover__header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.region-popover__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--color-primary, #3B5998);
  flex-shrink: 0;
}

.region-popover__name {
  flex: 1;
  font-size: 14px;
  font-weight: 700;
  color: var(--color-gray-800, #1A1F26);
}

.region-popover__close {
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: transparent;
  font-size: 20px;
  line-height: 1;
  color: var(--color-gray-500, #5C616B);
  cursor: pointer;
  border-radius: 4px;
}

.region-popover__close:hover {
  background: var(--color-gray-50, #F7F8FA);
  color: var(--color-gray-700, #2E333B);
}

.region-popover__metrics {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  margin-top: 12px;
}

.region-popover__metric {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.region-popover__metric-label {
  font-size: 11px;
  color: var(--color-gray-500, #5C616B);
  font-weight: 500;
}

.region-popover__metric-value {
  font-size: 22px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  line-height: 1;
}

.region-popover__metric-value.is-received { color: #4F4F4F; }
.region-popover__metric-value.is-completed { color: #2E7D32; }
.region-popover__metric-value.is-rate { color: #1A1A1A; }
.region-popover__metric-value.is-pending { color: #C62828; }

.region-popover__progress {
  height: 4px;
  background: var(--color-gray-100, #E8EAED);
  border-radius: 2px;
  margin-top: 12px;
  overflow: hidden;
}

.region-popover__progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #6366F1, #3B5998);
  border-radius: 2px;
  transition: width 0.3s ease;
}

/* ===== Mobile ===== */
@media (max-width: 767px) {
  .dashboard-panel {
    padding: 16px;
    border-radius: var(--radius-lg, 12px);
  }
  .heatmap-skeleton__map {
    width: 220px;
    height: 280px;
  }
  .region-popover,
  .region-popover.is-mobile {
    position: fixed;
    top: auto;
    left: 16px;
    right: 16px;
    bottom: 16px;
    width: auto;
    z-index: 100;
  }
  .region-popover__metric-value {
    font-size: 18px;
  }
}
</style>
